// Phase 33 — Lightweight skeleton primitive used everywhere the app used
// to render a spinner. Stateless, framework-agnostic, animate-pulse only.
//
// Usage:
//   <Skeleton className="h-4 w-32" />
//   <Skeleton.Block lines={3} />
//   <Skeleton.Card />  // full content-shaped block
//
// Visual rule: only one shade (`bg-slate-200/70 dark:bg-slate-700/40`) and
// only `rounded` corners. Anything fancier (gradient shimmers, multi-stage
// reveals) is anti-pattern — the goal is to communicate "structure is
// here, content is loading", not to look pretty.

import React from "react";

function Skeleton({ className = "", style }) {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className={`animate-pulse rounded bg-slate-200/70 ${className}`}
      style={style}
      data-testid="skeleton"
    />
  );
}

// Stacked text-line placeholder. `lines` controls count.
function Block({ lines = 3, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`} data-testid="skeleton-block">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className={`animate-pulse rounded bg-slate-200/70 ${
            i === lines - 1 ? "h-3 w-2/3" : "h-3 w-full"
          }`}
        />
      ))}
    </div>
  );
}

// Card-shaped composite: header bar + two text rows + KPI tile row.
function Card({ className = "" }) {
  return (
    <div
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}
      data-testid="skeleton-card"
    >
      <Skeleton className="h-3 w-24" />
      <Skeleton className="mt-3 h-7 w-32" />
      <Skeleton className="mt-2 h-3 w-40" />
    </div>
  );
}

// Table-row placeholder: 5 cells, last one narrower (matches our typical
// "verdict" column). `rows` controls how many to render.
function TableRows({ rows = 5, cols = 5, className = "" }) {
  return (
    <div className={`divide-y divide-slate-200 ${className}`} data-testid="skeleton-table">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-6 py-4">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton
              key={c}
              className={`h-3 ${
                c === 0
                  ? "w-48"
                  : c === cols - 1
                  ? "w-16"
                  : "flex-1"
              }`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

Skeleton.Block = Block;
Skeleton.Card = Card;
Skeleton.TableRows = TableRows;

export default Skeleton;
export { Skeleton };
